import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { searchSchema } from "@/domain/search/search.schema";
import { menuQueries } from "@/domain/menu/menu.query";
import { z } from "zod/v4";
import { MenuCard } from "./-components/MenuCard";
import { MenuCardSkeleton } from "./-components/MenuCardSkeleton";
import { useInView } from "react-intersection-observer";
import { useEffect, useMemo, memo, lazy, Suspense, Fragment } from "react";
import { createSEOHead, SEO_DATA, createFindMenusCitySEO } from "@/lib/seo";

const CustomRequestCta = lazy(() =>
  import("@/components/layouts/CustomRequestCta").then((module) => ({
    default: module.CustomRequestCta,
  })),
);

export const Route = createFileRoute("/(public)/trouver-un-chef/menus")({
  validateSearch: (search) => {
    const result = searchSchema.safeParse(search);
    if (!result.success) {
      const validFields: Record<string, any> = {};
      Object.keys(search || {}).forEach((key) => {
        const fieldResult = searchSchema.shape[
          key as keyof typeof searchSchema.shape
        ]?.safeParse(search[key]);
        if (fieldResult?.success) {
          validFields[key] = fieldResult.data;
        }
      });
      return validFields as z.infer<typeof searchSchema>;
    }
    return result.data;
  },
  loader: ({ context: { queryClient }, deps }) => {
    return queryClient.ensureQueryData(menuQueries.findMenus(deps));
  },
  head: ({ match }) => {
    if (match.search.city) {
      return createSEOHead(createFindMenusCitySEO(match.search.city));
    }
    return createSEOHead(SEO_DATA.FIND_MENUS);
  },
  component: RouteComponent,
});

// Mémoriser les skeletons pour éviter les re-renders
const InitialSkeletons = memo(() => {
  const skeletons = useMemo(
    () => Array.from({ length: 6 }, (_, i) => <MenuCardSkeleton key={i} />),
    [],
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {skeletons}
    </div>
  );
});

const LoadingSkeletons = memo(() => {
  const skeletons = useMemo(
    () =>
      Array.from({ length: 3 }, (_, i) => (
        <MenuCardSkeleton key={`loading-${i}`} />
      )),
    [],
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {skeletons}
    </div>
  );
});

function RouteComponent() {
  const search = Route.useSearch();
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "100px",
  });

  const {
    data,
    status,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(menuQueries.findMenus(search));

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (status === "pending") {
    return (
      <section className="container mx-auto px-4 py-6 max-w-6xl">
        <InitialSkeletons />
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="flex justify-center items-center min-h-64">
          <div className="text-red-500" role="alert">
            Erreur: {error?.message}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="container mx-auto px-4 py-6 max-w-6xl space-y-8">
      {data?.pages.map((page, pageIndex) => {
        let menuIndexOffset = 0;
        for (let p = 0; p < pageIndex; p++) {
          menuIndexOffset += data.pages[p].data.length;
        }

        return (
          <Fragment key={`page-${pageIndex}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {page.data.map((menu, menuIndex) => (
                <MenuCard
                  key={menu.id}
                  menu={menu}
                  index={menuIndexOffset + menuIndex}
                  isLCP={pageIndex === 0 && menuIndex === 0}
                />
              ))}
            </div>

            {pageIndex === 0 && page.data.length > 0 && (
              <Suspense fallback={null}>
                <CustomRequestCta title="menu" />
              </Suspense>
            )}
          </Fragment>
        );
      })}

      {hasNextPage && (
        <div ref={ref} className="flex justify-center py-4">
          {isFetchingNextPage ? <LoadingSkeletons /> : <div className="h-10" />}
        </div>
      )}
    </section>
  );
}
